import { cn } from "@/lib/utils";
import { Command } from "cmdk";
import type { DateRange, DateRangePreset } from "./types";
import { compareRanges } from "./shared";

type PresetsProps = {
  presets: DateRangePreset[];
  onSelect: (preset: DateRangePreset) => void;
  currentValue?: DateRange;
  currentPresetId?: string;
};

export function Presets({ presets, onSelect, currentValue, currentPresetId }: PresetsProps) {
  const matchesCurrent = (preset: DateRangePreset): boolean => {
    if (currentPresetId) {
      return currentPresetId === preset.id;
    }
    if (currentValue) {
      return compareRanges(currentValue, preset.dateRange);
    }
    return false;
  };

  return (
    <Command
      className="w-full rounded ring-ring ring-offset-2 focus:outline-none"
      tabIndex={0}
      autoFocus
      loop
    >
      <Command.List className="[&>*]:flex [&>*]:w-full [&>*]:items-start [&>*]:gap-x-2 [&>*]:gap-y-0.5 [&>*]:sm:flex-col">
        {presets.map((preset) => (
          <Command.Item
            key={preset.id}
            onSelect={() => onSelect(preset)}
            title={preset.label}
            value={preset.id}
            className={cn(
              "group relative flex cursor-pointer items-center justify-between overflow-hidden text-ellipsis whitespace-nowrap rounded border border-border",
              "px-2.5 py-1.5 text-left text-sm text-foreground/80 shadow-sm outline-none sm:w-full sm:border-none sm:py-2 sm:shadow-none",
              "disabled:pointer-events-none disabled:opacity-50",
              "sm:data-[selected=true]:bg-muted",
              matchesCurrent(preset) && "font-semibold text-foreground",
            )}
          >
            <span>{preset.label}</span>
            {preset.shortcut && (
              <kbd className="text-muted-foreground/70 hidden rounded bg-muted px-2 py-0.5 text-xs font-light group-data-[selected=true]:bg-accent md:block">
                {preset.shortcut.toUpperCase()}
              </kbd>
            )}
          </Command.Item>
        ))}
      </Command.List>
    </Command>
  );
}

Presets.displayName = "DatePicker.Presets";
